// ======================================================================
// File: RFQCompare.jsx  (ENHANCED VERSION)
// Module: PPE → Procurement → RFQ
//
// Purpose:
// - Show improved quotation comparison
// - Vendor-wise totals
// - Highlight lowest vendor
// - Professional ERP style matrix
// ======================================================================

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import rfqService from "../services/rfqService";

import "../rfq-ui.css";
import {
  formatCurrency,
  formatQuantity
} from "../../../../../../utils/formatUtils";

export default function RFQCompare() {

  const { rfqId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState([]);
  const [rfqDetail, setRfqDetail] = useState(null);

  // ==============================================================
  // LOAD DATA
  // ==============================================================

  useEffect(() => {
    if (rfqId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfqId]);

  const loadData = async () => {
    try {
      setLoading(true);

      const detailRes = await rfqService.getRFQById(rfqId);
      setRfqDetail(detailRes?.data?.data);

      const compareRes = await rfqService.getComparison(rfqId);
      setComparison(compareRes?.data?.data || []);

    } catch (err) {
      console.error("Failed to load comparison", err);
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // HELPERS
  // ==============================================================

  

  // ==============================================================
  // FINALIZE VENDOR
  // ==============================================================

  const handleFinalize = async (vendorId) => {

    if (!window.confirm("Are you sure to finalize this vendor?")) {
      return;
    }

    try {
      setLoading(true);

      await rfqService.finalizeVendor(rfqId, vendorId);

      alert("Vendor finalized successfully");

      navigate("/ppe/procurement/rfq");

    } catch (err) {

      const message =
        err?.response?.data?.message ||
        "Failed to finalize vendor";

      alert(message);

    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // GROUP DATA BY VENDOR
  // ==============================================================

  const grouped = comparison.reduce((acc, item) => {

    const key = item.rfq_vendor_id;

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);

    return acc;

  }, {});

  // ==============================================================
  // CALCULATE GRAND TOTALS
  // ==============================================================

  const vendorTotals = Object.keys(grouped).map(vendorKey => {

    const quotes = grouped[vendorKey];

    const total = quotes.reduce(
      (sum, q) => sum + parseFloat(q.total_amount || 0),
      0
    );

    return {
      vendorKey,
      vendorName: quotes[0]?.vendor_name || `Vendor #${vendorKey}`,
      total
    };

  });

  const lowestVendor = vendorTotals.reduce((prev, curr) =>
    prev.total < curr.total ? prev : curr,
    vendorTotals[0] || {}
  );

  // ==============================================================
  // RENDER
  // ==============================================================

  return (
    <div>

      <div className="rfq-header-card">
        <h2>RFQ Comparison</h2>
        <p>Compare vendor quotations and finalize</p>
      </div>

      {loading && (
        <div>Loading comparison...</div>
      )}

      {!loading && rfqDetail && (
        <div style={{ marginBottom: "15px" }}>
          <strong>RFQ No:</strong> {rfqDetail.rfq_no}
        </div>
      )}

      {!loading && Object.keys(grouped).length === 0 && (
        <div>No quotations available for comparison</div>
      )}

      {!loading &&
        Object.keys(grouped).map((vendorKey) => {

          const quotes = grouped[vendorKey];

          const grandTotal = quotes.reduce(
            (sum, q) => sum + parseFloat(q.total_amount || 0),
            0
          );

          const isLowest =
            lowestVendor.vendorKey === vendorKey;

          return (
            <div
              key={vendorKey}
              style={{
                marginBottom: "20px",
                background: "#fff",
                padding: "10px",
                borderRadius: "8px",
                border: isLowest
                  ? "2px solid green"
                  : "1px solid #ddd"
              }}
            >

              <div className="rfq-vendor-header">

                <div>
                  <h4>
                    {quotes[0]?.vendor_name ||
                      `Vendor #${vendorKey}`}
                  </h4>

                  <div className="rfq-vendor-total">
                    Total Quote :
                    {formatCurrency(grandTotal)}
                  </div>
                </div>

                {isLowest && (
                  <span className="rfq-badge-lowest">
                    Lowest Quote
                  </span>
                )}

              </div>

              <div className="rfq-table-wrapper">
                <table className="rfq-table">

                  <thead>
                    <tr>
                      <th>Material</th>
                      <th>Qty</th>
                      <th>UOM</th>
                      <th>Quoted Price</th>
                      <th>Tax</th>
                      <th>Total</th>
                      <th>Delivery Days</th>
                    </tr>
                  </thead>

                  <tbody>
                    {quotes.map((q, i) => (
                      <tr key={i}>
                        <td>
                          {q.material_name ||
                            `Material #${q.rfq_line_id}`}
                        </td>

                        <td>{formatQuantity(q.qty)}</td>
                        <td>{q.uom || "-"}</td>

                        <td>{formatCurrency(q.quoted_price)}</td>
                        <td>{formatCurrency(q.tax_amount)}</td>
                        <td>{formatCurrency(q.total_amount)}</td>

                        <td>{q.delivery_days || "-"}</td>
                      </tr>
                    ))}

                    <tr className="rfq-total-row">
                      <td colSpan="5">Grand Total</td>
                      <td colSpan="2">
                        {formatCurrency(grandTotal)}
                      </td>
                    </tr>

                  </tbody>

                </table>
              </div>

              <div className="rfq-vendor-footer">

                  {isLowest && (
                    <span className="rfq-badge-lowest">
                      Lowest Quote
                    </span>
                  )}

                  <button
                    className="btn-primary"
                    onClick={() =>
                      handleFinalize(quotes[0].vendor_id)
                    }
                  >
                    Finalize Vendor
                  </button>

                </div>

            </div>
          );
        })}

      <div style={{ marginTop: "15px" }}>
        <button
          className="btn-secondary"
          onClick={() => navigate("/ppe/procurement/rfq")}
        >
          Back to RFQ List
        </button>
      </div>

    </div>
  );
}
